import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Avatar,
  CircularProgress,
  TextField,
  MenuItem,
  ButtonGroup,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Divider,
} from "@material-ui/core";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import MultiRingChart from "../../Achievements/MultiRingChart";
import StackedRevenueChart from "../../Achievements/StackedRevenueChart";
import MiniLineChart from "../../Achievements/MiniLineChart";
import { useAchievementsStyles } from "../../Achievements/style";
import {
  getAdminSummaryAPI,
  getAdminRevenueAPI,
  getAdminTopCreatorsAPI,
  getAdminTopSpendersAPI,
  getAdminOverviewAPI,
  getAdminRetentionAPI,
  getAdminConversionAPI,
  getAdminSignupsTrendAPI,
  getAdminRevenueTrendAPI,
  getAdminMonetizationAPI,
  getAdminTransactionsAPI,
  getAdminComplianceAPI,
  getAdminSessionsNowAPI,
  getAdminPayoutsOverviewAPI,
  getAdminAvgEarningsAPI,
  getAdminActiveCreatorsTodayAPI,
  getAdminUploadVolumeAPI,
  getAdminIPsByUserAPI,
  getAdminUsersLastIPsAPI,
} from "../../../../../api/dashbordApi";
import { getFileByIdAPI } from "../../../../../api/userApi";
import { showToast } from "../../../../../utils/toast";
import defaultAvatars from "../../../../../assets/defaultAvatars.svg";
import { useHistory, useLocation } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  LabelList,
} from "recharts";
import { BarChart, Bar } from "recharts";
import MuiTooltip from "@material-ui/core/Tooltip";

import {
  ActiveSessionRow,
  ActiveTodayAPI,
  AvgEarnings,
  contexts,
  Granularity,
  IAdminOverview,
  IAdminRevenueMonth,
  IAdminSummary,
  ICompliance,
  IConversion,
  ICreatorActivity,
  IMonetization,
  IRetention,
  IRevenueTrendPoint,
  ITopCreator,
  ITopSpender,
  ITransactions,
  normalizeCompliance,
  PayoutsOverview,
  SignupsTrendPoint,
  TimePreset,
  UploadPoint,
  UserIpRow,
  UsersLastIpRow,
} from "./interfaceDashboard";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CloseIcon from "@material-ui/icons/Close";
import useSEO from "../../../../../seo/use-seo";
import StandardPageHelmet from "../../../../../seo/components/StandardPageHelmet";
import { normalizeVisitorApiPayload } from "../../../../../utils/visitorApi";
import AdminActiveSessionsPanel from "./AdminActiveSessionsPanel";

function normalizeActiveSessionRow(raw: any): ActiveSessionRow {
  return {
    userId: String(raw?.userId ?? raw?.id ?? ""),
    username: raw?.username,
    email: raw?.email,
    ip: raw?.ip ?? raw?.ipAddress ?? "",
    ua: raw?.ua ?? raw?.userAgent ?? "",
    lastSeenAt: raw?.lastSeenAt ?? raw?.last_seen_at ?? "",
    profileImg: raw?.profileImg ?? raw?.profile_img,
    visitor: normalizeVisitorApiPayload(
      raw?.visitor ??
        raw?.visitorApi ??
        raw?.visitorapi ??
        raw?.visitorData ??
        raw?.visitor_snapshot ??
        raw?.visitor_api
    ),
  };
}

function normalizeUsersLastIpRow(raw: any): UsersLastIpRow {
  return {
    userId: String(raw?.userId ?? raw?.id ?? ""),
    username: raw?.username,
    email: raw?.email,
    profileImg: raw?.profileImg ?? raw?.profile_img,
    lastAt: raw?.lastAt ?? raw?.last_at ?? "",
    ip: raw?.ip,
    ua: raw?.ua ?? raw?.userAgent,
    hits: Number(raw?.hits ?? 0),
    visitor: normalizeVisitorApiPayload(
      raw?.visitor ??
        raw?.visitorApi ??
        raw?.visitorapi ??
        raw?.visitorData ??
        raw?.visitor_snapshot ??
        raw?.visitor_api
    ),
  };
}

const AdminAnalyticsPage: React.FC = () => {
  const seo = useSEO("admin-dashboard");
  const classes = useAchievementsStyles();
  const now = new Date();
  const [year] = useState<string>(String(now.getFullYear()));
  const [from, setFrom] = useState<string>(
    new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10)
  );
  const [to, setTo] = useState<string>(
    new Date(now.getFullYear(), now.getMonth() + 1, 0)
      .toISOString()
      .slice(0, 10)
  );
  const [creatorId, setCreatorId] = useState<string>("");
  const [by, setBy] = useState<string>("");
  const [, setUser] = useState<{ firstName: string; lastName: string } | null>(
    null
  );
  const [summary, setSummary] = useState<IAdminSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState<boolean>(true);
  const [revenueMonths, setRevenueMonths] = useState<IAdminRevenueMonth[]>([]);
  const [revenueLoading, setRevenueLoading] = useState<boolean>(true);
  const [topCreators, setTopCreators] = useState<ITopCreator[]>([]);
  const [topSpenders, setTopSpenders] = useState<ITopSpender[]>([]);
  const [topLoading, setTopLoading] = useState<boolean>(true);
  const [overview, setOverview] = useState<IAdminOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState<boolean>(true);
  const [revGranularity, setRevGranularity] = useState<Granularity>("daily");
  const [revenueTrend, setRevenueTrend] = useState<IRevenueTrendPoint[]>([]);
  const [, setRevenueTrendLoading] = useState<boolean>(true);
  const [, setSignupsTrendLoading] = useState<boolean>(true);
  const [conversion, setConversion] = useState<IConversion | null>(null);
  const [, setConversionLoading] = useState<boolean>(true);
  const [retention, setRetention] = useState<IRetention | null>(null);
  const [, setRetentionLoading] = useState<boolean>(true);
  const [, setMonetization] = useState<IMonetization | null>(null);
  const [, setMonetizationLoading] = useState<boolean>(true);
  const [, setActivity] = useState<ICreatorActivity | null>(null);
  const [, setActivityLoading] = useState<boolean>(true);
  const [transactions, setTransactions] = useState<ITransactions | null>(null);
  const [transactionsLoading, setTransactionsLoading] = useState<boolean>(true);
  const [compliance, setCompliance] = useState<ICompliance | null>(null);
  const [complianceLoading, setComplianceLoading] = useState<boolean>(true);
  const [sessionsNow, setSessionsNow] = useState<ActiveSessionRow[]>([]);
  const [, setFailedLogins] = useState<
    Array<{ at: string; email: string; ip: string; reason: string }>
  >([]);
  const [securityLoading, setSecurityLoading] = useState<boolean>(true);
  const [signupsTrend, setSignupsTrend] = useState<SignupsTrendPoint[]>([]);
  const [, setTimePreset] = useState<TimePreset>("day"); // today | this week | this month
  const [signGranularity, setSignGranularity] = useState<Granularity>("daily");
  const [payouts, setPayouts] = useState<PayoutsOverview | null>(null);
  const [payoutsLoading, setPayoutsLoading] = useState<boolean>(true);
  const [avgEarnings, setAvgEarnings] = useState<AvgEarnings | null>(null);
  const [avgEarningsLoading, setAvgEarningsLoading] = useState<boolean>(true);
  const [activeCreatorsToday, setActiveCreatorsToday] = useState<number>(0);
  const [activeTodayLoading, setActiveTodayLoading] = useState<boolean>(true);
  const [uploadsGranularity, setUploadsGranularity] =
    useState<Granularity>("daily");
  const [uploads, setUploads] = useState<{
    totals: { photos: number; videos: number; total: number };
    points: UploadPoint[];
  } | null>(null);
  // const [visitorIps, setVisitorIps] = useState<VisitorIpRow[]>([]);
  const [userIpHistory, setUserIpHistory] = useState<UserIpRow[]>([]);
  const [userIpLoading, setUserIpLoading] = useState<boolean>(false);
  const [userIpError, setUserIpError] = useState<string | null>(null);
  const [userIpModal, setUserIpModal] = useState<{
    open: boolean;
    userId?: string;
    username?: string;
  }>({ open: false });
  const [, setUploadsLoading] = useState<boolean>(true);
  const [usersLastIPs, setUsersLastIPs] = useState<UsersLastIpRow[]>([]);
  const [showAllHistory, setShowAllHistory] = useState<boolean>(false);
  const [allHistoryLoading, setAllHistoryLoading] = useState<boolean>(false);
  const [allHistoryData, setAllHistoryData] = useState<ActiveSessionRow[]>([]);
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [expandedLastIpUsers, setExpandedLastIpUsers] = useState<Set<string>>(
    new Set()
  );
  const [userIpHistories, setUserIpHistories] = useState<
    Record<string, UserIpRow[]>
  >({});
  const [loadingIpHistories, setLoadingIpHistories] = useState<Set<string>>(
    new Set()
  );
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});
  const [loadingMedia, setLoadingMedia] = useState<Record<string, boolean>>({});
  const history = useHistory();
  const location = useLocation();
  const isUserAdmin = (u: any) =>
    !!(
      u?.isAdmin ||
      u?.role === "admin" ||
      u?.roles?.includes?.("admin") ||
      u?.userType?.title === "admin"
    );

  const [isAdmin, setIsAdmin] = useState(false);
  const fetchMediaUrl = async (mediaUrl?: string) => {
    if (!mediaUrl || mediaUrl === "defaultAvatars") return defaultAvatars;
    if (mediaUrls[mediaUrl]) return mediaUrls[mediaUrl];

    // Якщо це пряме посилання (https:// або http://), використовуємо його напряму
    if (mediaUrl.startsWith("https://") || mediaUrl.startsWith("http://")) {
      setMediaUrls(prev => ({ ...prev, [mediaUrl]: mediaUrl }));
      setLoadingMedia(prev => ({ ...prev, [mediaUrl]: false }));
      return mediaUrl;
    }

    setLoadingMedia(prev => ({ ...prev, [mediaUrl]: true }));
    try {
      const fileId = mediaUrl.split("/api/files/")[1];
      if (!fileId) throw new Error("Invalid file ID");
      const blobUrl = await getFileByIdAPI(fileId);
      setMediaUrls(prev => ({ ...prev, [mediaUrl]: blobUrl }));
      return blobUrl;
    } catch (err) {
      console.error(`Error fetching avatar for ${mediaUrl}:`, err);
      showToast({ message: "Error fetching avatar", err: true });
      return defaultAvatars;
    } finally {
      setLoadingMedia(prev => ({ ...prev, [mediaUrl]: false }));
    }
  };

  useEffect(() => {
    const all = [
      ...topCreators.map(x => x.profileImg).filter(Boolean),
      ...topSpenders.map(x => x.profileImg).filter(Boolean),
      ...sessionsNow.map(x => x.profileImg).filter(Boolean), // ⬅️ додали
      ...usersLastIPs.map(x => x.profileImg).filter(Boolean), // ⬅️ додали
      ...allHistoryData.map(x => x.profileImg).filter(Boolean), // ⬅️ додали для all history
    ] as string[];

    all.forEach(url => {
      if (!mediaUrls[url] && !loadingMedia[url]) fetchMediaUrl(url);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topCreators, topSpenders, sessionsNow, usersLastIPs, allHistoryData]);

  const toISO = (d: Date) =>
    new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString();
  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const endOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  const addDays = (d: Date, n: number) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
  const endOfMonth = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
  const startOfISOWeek = (d: Date) => {
    const res = startOfDay(d);
    const day = res.getDay();
    const diffToMon = (day + 6) % 7;
    return addDays(res, -diffToMon);
  };
  const endOfISOWeek = (d: Date) => addDays(startOfISOWeek(d), 6);
  const rangeFor = (
    g: "daily" | "weekly" | "monthly"
  ): { from: string; to: string } => {
    const now = new Date();
    if (g === "daily") {
      const to = endOfDay(now);
      const from = startOfDay(addDays(now, -29));
      return { from: toISO(from), to: toISO(to) };
    }
    if (g === "weekly") {
      const endW = endOfISOWeek(now);
      const startW = addDays(startOfISOWeek(now), -7 * 11);
      return { from: toISO(startW), to: toISO(endW) };
    }
    const endM = endOfMonth(now);
    const startM = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    return { from: toISO(startM), to: toISO(endM) };
  };
  const loadSummary = async () => {
    setSummaryLoading(true);
    try {
      const data = await getAdminSummaryAPI({
        from,
        to,
        month: undefined,
        year,
        creatorId: creatorId || undefined,
        by: by || undefined,
      });
      setSummary(data);
    } catch (e) {
      console.error("Admin summary error", e);
      showToast({ message: "Failed to load admin summary", err: true });
    } finally {
      setSummaryLoading(false);
    }
  };
  const loadOverview = async () => {
    setOverviewLoading(true);
    try {
      const data: IAdminOverview = await (getAdminOverviewAPI as any)({
        from,
        to,
        year,
        month: undefined,
        onlineWindow: "5m",
        by: by || undefined,
      });
      setOverview(data);
    } catch (e) {
      console.error("Admin overview error", e);
      showToast({ message: "Failed to load platform overview", err: true });
    } finally {
      setOverviewLoading(false);
    }
  };
  type RevenueTrendResponse = { points: IRevenueTrendPoint[] };
  const loadRevenueTrend = async (gran: Granularity = revGranularity) => {
    setRevenueTrendLoading(true);
    try {
      const r = rangeFor(gran);
      const data = (await (getAdminRevenueTrendAPI as any)({
        from: r.from,
        to: r.to,
        creatorId: creatorId || undefined,
        by: by || undefined,
        granularity: gran,
        tz,
        fillGaps: true,
        preset: undefined,
      })) as RevenueTrendResponse;
      setRevenueTrend(data.points || []);
    } catch (e) {
      console.error("Admin revenue-trend error", e);
      showToast({ message: "Failed to load revenue trend", err: true });
    } finally {
      setRevenueTrendLoading(false);
    }
  };
  const loadSignupsTrend = async (gran: Granularity = revGranularity) => {
    setSignupsTrendLoading(true);
    try {
      const r = rangeFor(gran);
      const data = (await (getAdminSignupsTrendAPI as any)({
        from: r.from,
        to: r.to,
        granularity: gran,
        tz,
        fillGaps: true,
        preset: undefined,
      })) as { points: SignupsTrendPoint[] };
      setSignupsTrend(data.points || []);
    } catch (e) {
      console.error("Admin signups-trend error", e);
      showToast({ message: "Failed to load signups trend", err: true });
    } finally {
      setSignupsTrendLoading(false);
    }
  };
  // const granToPreset = (g: Granularity): "day" | "week" | "month" =>
  //     g === "daily" ? "day" : g === "weekly" ? "week" : "month";
  const loadActiveCreatorsToday = async () => {
    setActiveTodayLoading(true);
    try {
      const data = (await (getAdminActiveCreatorsTodayAPI as any)({
        tz,
        since: "day", // або "15m", "24h" — неважливо, якщо бек ігнорує
      })) as ActiveTodayAPI;

      const n =
        data?.count ??
        data?.totalActiveUsersToday ??
        data?.totalActiveCreatorsToday ??
        0;

      setActiveCreatorsToday(n);
    } catch (e) {
      console.error("Admin active-today error", e);
      showToast({ message: "Failed to load active creators today", err: true });
    } finally {
      setActiveTodayLoading(false);
    }
  };
  const loadUploads = async (g: Granularity = revGranularity) => {
    setUploadsLoading(true);
    try {
      const r = rangeFor(g); // 30d / 12w / 12m
      const data = (await (getAdminUploadVolumeAPI as any)({
        from: r.from,
        to: r.to,
        granularity: g,
        tz,
        fillGaps: true,
        // важливо: не передавати preset
        preset: undefined,
      })) as {
        totals: { photos: number; videos: number; total: number };
        points: UploadPoint[];
      };

      setUploads(data || null);
    } catch (e) {
      console.error("Admin upload-volume error", e);
      showToast({ message: "Failed to load content upload volume", err: true });
    } finally {
      setUploadsLoading(false);
    }
  };

  const loadConversion = async (gran: Granularity = "daily") => {
    setConversionLoading(true);
    try {
      const r = rangeFor(gran === "monthly" ? "weekly" : gran);
      const data: IConversion = await (getAdminConversionAPI as any)({
        from: r.from,
        to: r.to,
        granularity: gran === "monthly" ? "weekly" : gran,
      });
      setConversion(data);
    } catch (e) {
      console.error("Admin conversion error", e);
      showToast({ message: "Failed to load conversion funnel", err: true });
    } finally {
      setConversionLoading(false);
    }
  };
  const loadPayoutsOverview = async () => {
    setPayoutsLoading(true);
    try {
      const data: PayoutsOverview = await (getAdminPayoutsOverviewAPI as any)({
        from,
        to,
        creatorId: creatorId || undefined,
        by: by || undefined,
      });
      setPayouts(data);
    } catch (e) {
      console.error("Admin payouts-overview error", e);
      showToast({ message: "Failed to load payouts overview", err: true });
    } finally {
      setPayoutsLoading(false);
    }
  };
  const loadAvgEarnings = async () => {
    setAvgEarningsLoading(true);
    try {
      const data: AvgEarnings = await (getAdminAvgEarningsAPI as any)({
        from,
        to,
        creatorId: creatorId || undefined,
        by: by || undefined,
      });
      setAvgEarnings(data);
    } catch (e) {
      console.error("Admin avg-earnings error", e);
      showToast({ message: "Failed to load average earnings", err: true });
    } finally {
      setAvgEarningsLoading(false);
    }
  };
  const loadRetention = async () => {
    setRetentionLoading(true);
    try {
      const data: IRetention = await (getAdminRetentionAPI as any)();
      setRetention(data);
    } catch (e) {
      console.error("Admin retention error", e);
      showToast({ message: "Failed to load retention", err: true });
    } finally {
      setRetentionLoading(false);
    }
  };
  const loadRevenue = async () => {
    setRevenueLoading(true);
    try {
      const data: { year: number; months: IAdminRevenueMonth[] } =
        await getAdminRevenueAPI({
          year,
          creatorId: creatorId || undefined,
          by: by || undefined,
        });
      setRevenueMonths(data.months || []);
    } catch (e) {
      console.error("Admin revenue error", e);
      showToast({ message: "Failed to load revenue", err: true });
    } finally {
      setRevenueLoading(false);
    }
  };
  const loadTops = async () => {
    setTopLoading(true);
    try {
      const [creators, spenders] = await Promise.all([
        (getAdminTopCreatorsAPI as any)({
          from,
          to,
          limit: 10,
          by: by || undefined,
        }),
        (getAdminTopSpendersAPI as any)({
          from,
          to,
          limit: 10,
          creatorId: creatorId || undefined,
          by: by || undefined,
        }),
      ]);
      setTopCreators(creators);
      setTopSpenders(spenders);
    } catch (e) {
      console.error("Admin tops error", e);
      showToast({ message: "Failed to load top lists", err: true });
    } finally {
      setTopLoading(false);
    }
  };
  const loadMonetization = async () => {
    setMonetizationLoading(true);
    try {
      const data: IMonetization = await (getAdminMonetizationAPI as any)({
        from,
        to,
        creatorId: creatorId || undefined,
        by: by || undefined,
      });
      setMonetization(data);
    } catch (e) {
      console.error("Admin monetization error", e);
      showToast({
        message: "Failed to load payments & monetization",
        err: true,
      });
    } finally {
      setMonetizationLoading(false);
    }
  };
  const loadCreatorActivity = async () => {
    setActivityLoading(true);
    try {
      const data: ICreatorActivity = await (
        getAdminActiveCreatorsTodayAPI as any
      )({
        tz, // обов'язково
        since: "1d", // опційно: 5m | 15m | 1h | 1d ...
        preset: "day", // опційно
      });
      setActivity(data);
    } catch (e) {
      console.error("Admin creator-activity error", e);
      showToast({ message: "Failed to load creator activity", err: true });
    } finally {
      setActivityLoading(false);
    }
  };
  const loadTransactions = async () => {
    setTransactionsLoading(true);
    try {
      const data: ITransactions = await getAdminTransactionsAPI({
        creatorId: creatorId || undefined,
        by: by || undefined,
      });
      setTransactions(data);
    } catch (e) {
      console.error("Admin transactions error", e);
      showToast({
        message: "Failed to load transaction monitoring",
        err: true,
      });
    } finally {
      setTransactionsLoading(false);
    }
  };
  const loadCompliance = async () => {
    setComplianceLoading(true);
    try {
      const raw = await getAdminComplianceAPI({ days: 30 });
      setCompliance(normalizeCompliance(raw));
    } catch (e) {
      console.error("Admin compliance error", e);
      showToast({ message: "Failed to load compliance snapshot", err: true });
    } finally {
      setComplianceLoading(false);
    }
  };
  const loadSecurity = async () => {
    setSecurityLoading(true);
    try {
      const [sNow, usersLast] = await Promise.all([
        getAdminSessionsNowAPI({ since: "30m" }), // Changed from 5m to 30m
        getAdminUsersLastIPsAPI({ since: "30d", limit: 500 }),
      ]);

      setSessionsNow(
        Array.isArray(sNow) ? sNow.map(normalizeActiveSessionRow) : []
      );
      setUsersLastIPs(
        Array.isArray(usersLast) ? usersLast.map(normalizeUsersLastIpRow) : []
      );
      setFailedLogins([]);
    } catch (e) {
      console.error("Admin security error", e);
      showToast({ message: "Failed to load security logs", err: true });
    } finally {
      setSecurityLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
    loadSummary();
    loadRevenue();
    loadTops();
    loadMonetization();
    loadCreatorActivity();
    loadTransactions();
    loadCompliance();
    loadSecurity();
    loadRevenueTrend(revGranularity);
    loadSignupsTrend(signGranularity);
    loadConversion(revGranularity);
    loadRetention();
    loadPayoutsOverview();
    loadAvgEarnings();
    loadActiveCreatorsToday();
    loadUploads(uploadsGranularity);
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setIsAdmin(isUserAdmin(parsedUser));
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadRevenueTrend(revGranularity);
    loadSignupsTrend(signGranularity);
    loadConversion(revGranularity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revGranularity, by, creatorId, tz]);

  const applyFilters = () => {
    loadOverview();
    loadSummary();
    loadRevenue();
    loadTops();
    loadMonetization();
    loadCreatorActivity();
    loadTransactions();
    loadCompliance();
    loadSecurity();
    loadRevenueTrend(revGranularity);
    loadSignupsTrend(signGranularity);
    loadConversion(revGranularity);
    loadPayoutsOverview();
    loadAvgEarnings();
  };

  const donutData = useMemo(() => {
    const tips = summary?.breakdown?.tips || 0;
    const subs = summary?.breakdown?.subscriptions || 0;
    const custom = summary?.breakdown?.customVideo || 0;
    const posts = summary?.breakdown?.postPurchase || 0;
    const total = tips + subs + custom + posts;
    return {
      tips,
      subscriptions: subs,
      customVideoRequests: custom,
      sales: posts,
      total,
      loading: summaryLoading,
    };
  }, [summary, summaryLoading]);

  const stackedRevenue = useMemo(() => {
    const y = parseInt(year, 10);
    const toMonthNum = (row: any) => {
      const m = row?.month;
      if (typeof m === "number") return m;
      if (typeof m === "string") {
        if (m.includes("-")) return parseInt(m.split("-")[1], 10); // 'YYYY-MM'
        const n = parseInt(m, 10);
        if (!Number.isNaN(n)) return n;
      }
      return undefined;
    };
    const byMonth = new Map<number, any>();
    for (const r of revenueMonths) {
      const n = toMonthNum(r);
      if (n && n >= 1 && n <= 12) byMonth.set(n, r);
    }
    return Array.from({ length: 12 }, (_, i) => {
      const m = i + 1;
      const src = byMonth.get(m);
      const b = src?.breakdown ?? {};
      return {
        month: `${y}-${String(m).padStart(2, "0")}`,
        label: new Date(y, m - 1, 1).toLocaleString(undefined, {
          month: "short",
        }), // для тултіпів/легенди
        tips: b.tip ?? 0,
        subscriptions: b.subscription ?? 0,
        customVideoRequests: b["custom-video"] ?? 0,
        sales: b["post-purchase"] ?? 0,
        postPurchases: b["post-purchase"] ?? 0, // ← було "sales"
      };
    });
  }, [revenueMonths, year]);
  const totalRevenue = useMemo(
    () =>
      stackedRevenue.reduce(
        (s, m) =>
          s + m.tips + m.subscriptions + m.customVideoRequests + m.sales,
        0
      ),
    [stackedRevenue]
  );
  const setUploadsMode = (mode: Granularity) => {
    setUploadsGranularity(mode);
    loadUploads(mode); // одразу тягнемо нові дані
  };

  const formatCurrency = (v: number) =>
    v >= 1_000_000
      ? `$${(v / 1_000_000).toFixed(1)}M`
      : v >= 1_000
        ? `$${(v / 1_000).toFixed(1)}K`
        : `$${v.toFixed(2)}`;

  const periodLabel = useMemo(() => {
    const y = parseInt(year, 10);
    return `Jan ${y} - Dec ${y}`;
  }, [year]);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setIsAdmin(isUserAdmin(parsedUser));
      } catch (err) {
        console.error("Failed to parse user from localStorage", err);
      }
    }
  }, []);
  const setRevenueMode = (mode: Granularity) => {
    setRevGranularity(mode);
    const p: TimePreset =
      mode === "daily" ? "day" : mode === "weekly" ? "week" : "month";
    setTimePreset(p);
    const r = rangeFor(mode); // <-- було rangeFor(p)
    setFrom(r.from);
    setTo(r.to);
  };
  // const revenueTotal = useMemo(
  //     () => (revenueTrend || []).reduce((s, p) => s + (p?.total || 0), 0),
  //     [revenueTrend]
  // );
  const setSignupsMode = (mode: Granularity) => {
    setSignGranularity(mode);
    loadSignupsTrend(mode);
  };
  const revenueChartData = useMemo(
    () => revenueTrend.map(p => ({ label: p.label, total: p.total || 0 })),
    [revenueTrend]
  );
  const signupsChartData = useMemo(
    () => signupsTrend.map(p => ({ label: p.label, count: p.count || 0 })),
    [signupsTrend]
  );

  const base = retention?.baseCreators90d ?? retention?.baseUsers90d ?? 0;
  const r = retention?.rates ?? { r30: 0, r60: 0, r90: 0 };
  const c = retention?.counts ?? { n30: 0, n60: 0, n90: 0 };

  const RetentionTooltip: React.FC<any> = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const { pct, count } = payload[0].payload;
    return (
      <Box
        style={{
          padding: 8,
          background: "#0B0B0F",
          border: "1px solid #27272a",
          borderRadius: 12,
        }}
      >
        <div style={{ fontWeight: 600 }}>{label}</div>
        <div style={{ opacity: 0.9 }}>
          {pct}% ({count}/{base} active)
        </div>
      </Box>
    );
  };
  const retentionData = [
    { label: "30d", pct: Math.round(r.r30), count: c.n30 },
    { label: "60d", pct: Math.round(Math.max(r.r60, r.r30)), count: c.n60 },
    { label: "90d", pct: Math.round(Math.max(r.r90, r.r60)), count: c.n90 },
  ];

  const txn24hTotals = useMemo(() => {
    const a = transactions?.volume24h ?? [];
    return {
      total: a.reduce((s, x) => s + (x.total || 0), 0),
      success: a.reduce((s, x) => s + (x.success || 0), 0),
      failed: a.reduce((s, x) => s + (x.failed || 0), 0),
    };
  }, [transactions]);

  const txn7dTotals = useMemo(() => {
    const a = transactions?.volume7d ?? [];
    return {
      total: a.reduce((s, x) => s + (x.total || 0), 0),
      success: a.reduce((s, x) => s + (x.success || 0), 0),
      failed: a.reduce((s, x) => s + (x.failed || 0), 0),
    };
  }, [transactions]);

  const txn24hChartData = useMemo(
    () =>
      (transactions?.volume24h ?? []).map(r => ({
        label: r.bucket,
        success: r.success,
        failed: r.failed,
      })),
    [transactions]
  );

  const txn7dChartData = useMemo(
    () =>
      (transactions?.volume7d ?? []).map(r => ({
        label: r.day,
        success: r.success,
        failed: r.failed,
      })),
    [transactions]
  );
  const openUserIpHistory = async (userId: string, username?: string) => {
    setUserIpModal({ open: true, userId, username });
    setUserIpLoading(true);
    setUserIpError(null);
    try {
      const rows = await (getAdminIPsByUserAPI as any)({ userId, limit: 100 });
      setUserIpHistory((rows as UserIpRow[]) || []);
    } catch (e) {
      setUserIpError((e as Error)?.message || "Failed to load user IP history");
    } finally {
      setUserIpLoading(false);
    }
  };

  const closeUserIpHistory = () => {
    setUserIpModal({ open: false });
    setUserIpHistory([]);
    setUserIpError(null);
  };

  const loadAllHistory = async () => {
    setAllHistoryLoading(true);
    try {
      const allHistory = await getAdminSessionsNowAPI({ since: "24h" }); // Load last 24 hours
      setAllHistoryData(
        Array.isArray(allHistory)
          ? allHistory.map(normalizeActiveSessionRow)
          : []
      );
    } catch (e) {
      console.error("Admin all history error", e);
      showToast({ message: "Failed to load all history", err: true });
    } finally {
      setAllHistoryLoading(false);
    }
  };

  const toggleShowAllHistory = () => {
    if (!showAllHistory && allHistoryData.length === 0) {
      loadAllHistory();
    }
    setShowAllHistory(!showAllHistory);
  };

  const toggleLastIpUserExpansion = (userId: string) => {
    setExpandedLastIpUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleUserExpansion = async (userId: string) => {
    const isExpanded = expandedUsers.has(userId);

    if (isExpanded) {
      // Collapse user
      setExpandedUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    } else {
      // Expand user and load IP history if not already loaded
      setExpandedUsers(prev => new Set(prev).add(userId));

      if (!userIpHistories[userId]) {
        setLoadingIpHistories(prev => new Set(prev).add(userId));
        try {
          const rows = await (getAdminIPsByUserAPI as any)({
            userId,
            limit: 100,
          });
          setUserIpHistories(prev => ({
            ...prev,
            [userId]: (rows as UserIpRow[]) || [],
          }));
        } catch (e) {
          console.error(`Error loading IP history for user ${userId}:`, e);
          showToast({ message: "Failed to load IP history", err: true });
        } finally {
          setLoadingIpHistories(prev => {
            const newSet = new Set(prev);
            newSet.delete(userId);
            return newSet;
          });
        }
      }
    }
  };

  const creatorsKPI = compliance?.kpis?.creators18Plus ?? {
    verified: 0,
    total: 0,
    percent: 0,
  };
  const verifiedCreators = Number(creatorsKPI.verified || 0);
  const totalCreators = Number(creatorsKPI.total || 0);

  const verifiedPercent = Number.isFinite(Number(creatorsKPI.percent))
    ? Number(creatorsKPI.percent)
    : totalCreators > 0
      ? (verifiedCreators * 100) / totalCreators
      : 0;

  const dmcaCount = compliance?.kpis?.dmcaRecentCount ?? 0;
  const uptimePct = Number(
    compliance?.kpis?.uptimePercent ?? compliance?.downtime?.percent ?? 0
  );
  // 2257
  //     const lastAuditAt = compliance?.custodian2257?.lastAuditAt ?? null;
  //     const missingDocs = compliance?.custodian2257?.missingDocsCount ?? 0;
  //     const custodianRecent = compliance?.custodian2257?.recent ?? [];
  // Downtime таблиця
  //     const downtimeDays = compliance?.downtime?.days ?? 30;
  //     const downtimeEvents = compliance?.downtime?.events ?? [];

  const fmtDateTime = (iso?: string) =>
    iso ? new Date(iso).toLocaleString(undefined, { hour12: false }) : "-";

  const copyToClipboard = (txt: string) =>
    navigator.clipboard?.writeText?.(txt).catch(() => {});

  // const uploadsChartData = useMemo(
  //     () => (uploads?.points || []).map(p => ({
  //         label: p.label,
  //         photos: p.photos || 0,
  //         videos: p.videos || 0,
  //         total:  p.total  || 0,
  //     })),
  //     [uploads]
  // );
  const uploadsChartData = useMemo(() => uploads?.points ?? [], [uploads]);
  // const uploadsTotals = uploads?.totals ?? { photos: 0, videos: 0, total: 0 };

  const UploadsTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const get = (k: string) =>
      payload.find((p: any) => p.dataKey === k)?.value ?? 0;
    const photos = get("photos");
    const videos = get("videos");
    const total = get("total") ?? photos + videos;

    return (
      <div
        style={{
          background: "#09090b",
          border: "1px solid #27272a",
          borderRadius: 12,
          padding: "8px 10px",
          fontSize: 12,
          color: "#fff",
        }}
      >
        <div style={{ opacity: 0.7, marginBottom: 6 }}>{label}</div>
        <div>Photos: {photos}</div>
        <div>Videos: {videos}</div>
        <div style={{ opacity: 0.9 }}>Total: {total}</div>
      </div>
    );
  };

  return (
    <>
      <StandardPageHelmet seo={seo} />
      <Box className={classes.container}>
        {/* Admin-only switcher */}
        {isAdmin && (
          <Box className={classes.analyticsSwitcher}>
            <ButtonGroup variant="outlined" size="small">
              <Button
                className={classes.analyticsTab}
                variant={
                  !location.pathname.startsWith("/adminDashboard")
                    ? "contained"
                    : "outlined"
                }
                onClick={() => history.push("/dashboard")}
              >
                Creator analytics
              </Button>
              <Button
                className={classes.analyticsTab}
                variant={
                  location.pathname.startsWith("/adminDashboard")
                    ? "contained"
                    : "outlined"
                }
                onClick={() => history.push("/adminDashboard")}
              >
                Admin analytics
              </Button>
            </ButtonGroup>
          </Box>
        )}
        <div className={classes.containerr}>
          {/* Header */}
          <Typography className={classes.welcome}>Admin Analytics</Typography>
          <Typography className={classes.subtitle}>
            Monitor platform-wide earnings and creator performance
          </Typography>
          {/* Filters */}

          {/* Platform Overview (Top KPIs at a Glance) */}
          <Grid
            className={classes.monthlyEarningsGrid}
            style={{ marginTop: 16 }}
          >
            <Typography className={classes.monthlyEarningsGridTitle}>
              Platform Overview
            </Typography>
            <div className={classes.monthlyEarningsGriddHelpp}>
              <Grid
                className={classes.monthlyEarningsGridCard}
              >
                <Box className={classes.statCard}>
                  <Typography className={classes.cardTitle}>
                    Total Online Creators
                  </Typography>
                  <div className={classes.cardBottom}>
                    <div className={classes.cardHelp}>
                      <Typography className={classes.cardValue}>
                        {overviewLoading
                          ? "..."
                          : (
                              overview?.kpis.totalOnlineCreators ?? 0
                            ).toLocaleString()}
                      </Typography>
                      <Typography className={classes.cardChange}>
                        active last 5 min
                      </Typography>
                    </div>
                  </div>
                </Box>
              </Grid>
              <Grid
                className={classes.monthlyEarningsGridCard}
              >
                <Box className={classes.statCard}>
                  <Typography className={classes.cardTitle}>
                    Total Active Creators
                  </Typography>
                  <div className={classes.cardBottom}>
                    <div className={classes.cardHelp}>
                      <Typography className={classes.cardValue}>
                        {overviewLoading
                          ? "..."
                          : (
                              overview?.kpis.totalActiveCreators ?? 0
                            ).toLocaleString()}
                      </Typography>
                      <Typography className={classes.cardChange}>
                        with activity or revenue
                      </Typography>
                    </div>
                  </div>
                </Box>
              </Grid>
              <Grid
                className={classes.monthlyEarningsGridCard}
              >
                <Box className={classes.statCard}>
                  <Typography className={classes.cardTitle}>
                    Total Registered Users
                  </Typography>
                  <div className={classes.cardBottom}>
                    <div className={classes.cardHelp}>
                      <Typography className={classes.cardValue}>
                        {overviewLoading
                          ? "..."
                          : (
                              overview?.kpis.totalRegisteredUsers ?? 0
                            ).toLocaleString()}
                      </Typography>
                    </div>
                  </div>
                </Box>
              </Grid>
              <Grid
                className={classes.monthlyEarningsGridCard}
              >
                <Box className={classes.statCard}>
                  <Typography className={classes.cardTitle}>
                    Monthly Active Users (MAU)
                  </Typography>
                  <div className={classes.cardBottom}>
                    <div className={classes.cardHelp}>
                      <Typography className={classes.cardValue}>
                        {overviewLoading
                          ? "..."
                          : (overview?.kpis.mau30d ?? 0).toLocaleString()}
                      </Typography>
                    </div>
                  </div>
                </Box>
              </Grid>
              <Grid
                className={classes.monthlyEarningsGridCard}
              >
                <Box className={classes.statCard}>
                  <Typography className={classes.cardTitle}>
                    Total Revenue (This Month)
                  </Typography>
                  <div className={classes.cardBottom}>
                    <div className={classes.cardHelp}>
                      <Typography className={classes.cardValue}>
                        {overviewLoading
                          ? "..."
                          : `$${(
                              overview?.kpis.revenueThisMonth ?? 0
                            ).toLocaleString()}`}
                      </Typography>
                    </div>
                  </div>
                </Box>
              </Grid>
              <Grid
                className={classes.monthlyEarningsGridCard}
              >
                <Box className={classes.statCard}>
                  <Typography className={classes.cardTitle}>
                    Creator Payouts (This Month)
                  </Typography>
                  <div className={classes.cardBottom}>
                    <div className={classes.cardHelp}>
                      <Typography className={classes.cardValue}>
                        {overviewLoading
                          ? "..."
                          : `$${(
                              overview?.kpis.payoutsThisMonth ?? 0
                            ).toLocaleString()}`}
                      </Typography>
                    </div>
                  </div>
                </Box>
              </Grid>
              <Grid
                className={classes.monthlyEarningsGridCard}
              >
                <Box className={classes.statCard}>
                  <Typography className={classes.cardTitle}>
                    Pending Payouts
                  </Typography>
                  <div className={classes.cardBottom}>
                    <div className={classes.cardHelp}>
                      <Typography className={classes.cardValue}>
                        {overviewLoading
                          ? "..."
                          : `$${(
                              overview?.kpis.pendingPayouts.amount ?? 0
                            ).toLocaleString()}`}
                      </Typography>
                      <Typography className={classes.cardChange}>
                        {overviewLoading
                          ? ""
                          : `${
                              overview?.kpis.pendingPayouts.count ?? 0
                            } requests`}
                      </Typography>
                    </div>
                  </div>
                </Box>
              </Grid>
              <Grid
                className={classes.monthlyEarningsGridCard}
              >
                <Box className={classes.statCard}>
                  <Typography className={classes.cardTitle}>
                    New Signups
                  </Typography>
                  <div className={classes.cardBottom}>
                    <div className={classes.cardHelp}>
                      <Typography
                        style={{ fontSize: "24px" }}
                        className={classes.cardValue}
                      >
                        {overviewLoading
                          ? "..."
                          : `Today: ${(
                              overview?.kpis.newSignups.today ?? 0
                            ).toLocaleString()} • Week: ${(
                              overview?.kpis.newSignups.week ?? 0
                            ).toLocaleString()}`}
                      </Typography>
                    </div>
                  </div>
                </Box>
              </Grid>
            </div>
          </Grid>
          <AdminActiveSessionsPanel
            classes={{
              subscribersBox: classes.subscribersBox,
              sectionTitle: classes.sectionTitle,
              subscribersBoxx: classes.subscribersBoxx,
              subscriberItem: classes.subscriberItem,
              subscriberItemm: classes.subscriberItemm,
              avatar: classes.avatar,
              name: classes.name,
              username: classes.username,
            }}
            securityLoading={securityLoading}
            allHistoryLoading={allHistoryLoading}
            showAllHistory={showAllHistory}
            toggleShowAllHistory={toggleShowAllHistory}
            sessionsNow={sessionsNow}
            allHistoryData={allHistoryData}
            expandedUsers={expandedUsers}
            toggleUserExpansion={toggleUserExpansion}
            userIpHistories={userIpHistories}
            loadingIpHistories={loadingIpHistories}
            expandedLastIpUsers={expandedLastIpUsers}
            toggleLastIpUserExpansion={toggleLastIpUserExpansion}
            usersLastIPs={usersLastIPs}
            mediaUrls={mediaUrls}
            loadingMedia={loadingMedia}
            fmtDateTime={fmtDateTime}
            openUserIpHistory={openUserIpHistory}
          />
          {/* Creator Analytics */}
          <Grid className={classes.monthlyEarningsGrid}>
            <Typography className={classes.monthlyEarningsGridTitle}>
              Creator Analytics
            </Typography>
            <div
              className={classes.monthlyEarningsGridd}
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 16,
                flexDirection: "row",
              }}
            >
              <Grid
                className={classes.monthlyEarningsGridCard}
                style={{ flex: "1 1 100%", maxWidth: "100%" }}
              >
                <Box style={{ height: 350 }} className={classes.statCard}>
                  <div
                    className={classes.cardHelpTop}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography className={classes.cardTitle}>
                      Creator Revenue —{" "}
                      {revGranularity[0].toUpperCase() +
                        revGranularity.slice(1)}
                    </Typography>
                    <ButtonGroup
                      size="small"
                      variant="outlined"
                      className={classes.butTimeGroup}
                    >
                      <Button
                        className={classes.butTime}
                        onClick={() => setRevenueMode("daily")}
                        variant={
                          revGranularity === "daily" ? "contained" : "outlined"
                        }
                      >
                        Daily
                      </Button>
                      <Button
                        className={classes.butTime}
                        onClick={() => setRevenueMode("weekly")}
                        variant={
                          revGranularity === "weekly" ? "contained" : "outlined"
                        }
                      >
                        Weekly
                      </Button>
                      <Button
                        className={classes.butTime}
                        onClick={() => setRevenueMode("monthly")}
                        variant={
                          revGranularity === "monthly"
                            ? "contained"
                            : "outlined"
                        }
                      >
                        Monthly
                      </Button>
                    </ButtonGroup>
                  </div>
                  <div
                    style={{ display: "flex", gap: 24, alignItems: "flex-end" }}
                  >
                    <div style={{ flex: 1, height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={revenueChartData}
                          margin={{ top: 4, right: 12, left: 8, bottom: 28 }}
                        >
                          <CartesianGrid
                            stroke="#27272a"
                            strokeDasharray="3 3"
                          />
                          <XAxis
                            dataKey="label"
                            interval={0}
                            angle={-45}
                            height={34}
                            textAnchor="end"
                            tick={{ fontSize: 12, fill: "#a1a1aa" }}
                          />
                          <YAxis tick={{ fontSize: 12, fill: "#a1a1aa" }} />
                          <RechartsTooltip
                            contentStyle={{
                              background: "#0B0B0F",
                              border: "1px solid #27272a",
                              borderRadius: 12,
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="total"
                            stroke="#F472B6"
                            strokeWidth={2}
                            dot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </Box>
              </Grid>
            </div>
          </Grid>

          {/* Growth Trends */}
          <Grid className={classes.monthlyEarningsGrid}>
            <Typography className={classes.monthlyEarningsGridTitle}>
              Growth Trends
            </Typography>
            <div
              className={classes.monthlyEarningsGridd}
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 16,
                flexDirection: "row",
              }}
            >
              <Grid
                className={classes.monthlyEarningsGridCard}
                style={{ flex: "1 1 100%", maxWidth: "100%" }}
              >
                <Box style={{ height: 350 }} className={classes.statCard}>
                  <div
                    className={classes.cardHelpTop}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography className={classes.cardTitle}>
                      Revenue —{" "}
                      {revGranularity[0].toUpperCase() +
                        revGranularity.slice(1)}
                    </Typography>
                    <ButtonGroup
                      size="small"
                      variant="outlined"
                      className={classes.butTimeGroup}
                    >
                      <Button
                        className={classes.butTime}
                        onClick={() => setRevenueMode("daily")}
                        variant={
                          revGranularity === "daily" ? "contained" : "outlined"
                        }
                      >
                        Daily
                      </Button>
                      <Button
                        className={classes.butTime}
                        onClick={() => setRevenueMode("weekly")}
                        variant={
                          revGranularity === "weekly" ? "contained" : "outlined"
                        }
                      >
                        Weekly
                      </Button>
                      <Button
                        className={classes.butTime}
                        onClick={() => setRevenueMode("monthly")}
                        variant={
                          revGranularity === "monthly"
                            ? "contained"
                            : "outlined"
                        }
                      >
                        Monthly
                      </Button>
                    </ButtonGroup>
                  </div>
                  <div
                    style={{ display: "flex", gap: 24, alignItems: "flex-end" }}
                  >
                    {/*<div className={classes.cardHelp}>*/}
                    {/*    <Typography className={classes.cardValue}>*/}
                    {/*        {revenueTrendLoading ? "..." : formatCurrency(revenueTotal)}*/}
                    {/*    </Typography>*/}
                    {/*    <Typography className={classes.cardChange}>*/}
                    {/*        <TrendingUpIcon fontSize="small" /> total {revGranularity}*/}
                    {/*    </Typography>*/}
                    {/*</div>*/}
                    <div style={{ flex: 1, height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={revenueChartData}
                          margin={{ top: 4, right: 12, left: 8, bottom: 28 }}
                        >
                          <CartesianGrid
                            stroke="#27272a"
                            strokeDasharray="3 3"
                          />
                          <XAxis
                            dataKey="label"
                            interval={0}
                            angle={-45}
                            height={34}
                            textAnchor="end"
                            tick={{ fontSize: 12, fill: "#a1a1aa" }}
                          />
                          <YAxis tick={{ fontSize: 12, fill: "#a1a1aa" }} />
                          <RechartsTooltip
                            contentStyle={{
                              background: "#0B0B0F",
                              border: "1px solid #27272a",
                              borderRadius: 12,
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="total"
                            stroke="#F472B6"
                            strokeWidth={2}
                            dot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </Box>
              </Grid>
              <Grid
                className={classes.monthlyEarningsGridCard}
                style={{ flex: "1 1 100%", maxWidth: "100%" }}
              >
                <Box style={{ height: 350 }} className={classes.statCard}>
                  <div
                    className={classes.cardHelpTop}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography className={classes.cardTitle}>
                      New User Signups Over Time
                    </Typography>
                    <ButtonGroup
                      size="small"
                      variant="outlined"
                      className={classes.butTimeGroup}
                    >
                      <Button
                        className={classes.butTime}
                        onClick={() => setSignupsMode("daily")}
                        variant={
                          signGranularity === "daily" ? "contained" : "outlined"
                        }
                      >
                        Daily
                      </Button>
                      <Button
                        className={classes.butTime}
                        onClick={() => setSignupsMode("weekly")}
                        variant={
                          signGranularity === "weekly"
                            ? "contained"
                            : "outlined"
                        }
                      >
                        Weekly
                      </Button>
                      <Button
                        className={classes.butTime}
                        onClick={() => setSignupsMode("monthly")}
                        variant={
                          signGranularity === "monthly"
                            ? "contained"
                            : "outlined"
                        }
                      >
                        Monthly
                      </Button>
                    </ButtonGroup>
                  </div>
                  <div
                    style={{ display: "flex", gap: 24, alignItems: "flex-end" }}
                  >
                    {/*<div className={classes.cardHelp}>*/}
                    {/*    <Typography className={classes.cardValue}>*/}
                    {/*        {signupsTrendLoading ? "..." : signupsTrend.reduce((a, p) => a + (p.count || 0), 0).toLocaleString()}*/}
                    {/*    </Typography>*/}
                    {/*    <Typography className={classes.cardChange}>signups {signGranularity}</Typography>*/}
                    {/*</div>*/}
                    <div style={{ flex: 1, height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={signupsChartData}
                          margin={{ top: 4, right: 12, left: 8, bottom: 28 }}
                        >
                          <CartesianGrid
                            stroke="#27272a"
                            strokeDasharray="3 3"
                          />
                          <XAxis
                            dataKey="label"
                            interval={0}
                            angle={-45}
                            height={34}
                            textAnchor="end"
                            tick={{ fontSize: 12, fill: "#a1a1aa" }}
                          />
                          <YAxis tick={{ fontSize: 12, fill: "#a1a1aa" }} />
                          <RechartsTooltip
                            contentStyle={{
                              background: "#09090b",
                              border: "1px solid #27272a",
                              borderRadius: 12,
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="count"
                            stroke="#60A5FA"
                            strokeWidth={2}
                            dot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </Box>
              </Grid>
              <Grid container spacing={3} className={classes.centre}>
                {/* ------- Conversion: full width ------- */}
                <Grid item xs={12} className={classes.monthlyEarningsGridCard}>
                  <Box className={classes.statCardHelpc}>
                    <Typography className={classes.cardTitle}>
                      Conversion Funnel
                    </Typography>
                    <Grid container spacing={1}>
                      {[
                        { label: "Visits", val: conversion?.totals.visits },
                        { label: "Signups", val: conversion?.totals.signups },
                        { label: "Paid", val: conversion?.totals.paid },
                      ].map(k => (
                        <Grid item xs={4} key={k.label}>
                          <Box>
                            <div>{k.label}</div>
                            <div>{(k.val ?? 0).toLocaleString()}</div>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                    <Box mt={1} display="grid" gridTemplateColumns="1fr 1fr">
                      <Chip
                        label={`Signup rate: ${Math.round(
                          (conversion?.totals.signupRate || 0) * 100
                        )}%`}
                      />
                      <Chip
                        label={`First-pay rate: ${Math.round(
                          (conversion?.totals.firstPayRate || 0) * 100
                        )}%`}
                      />
                    </Box>
                  </Box>
                </Grid>
                {/* ------- Retention: full width ------- */}
                <Grid item xs={12} className={classes.monthlyEarningsGridCard}>
                  <Box className={classes.statCardHelpc}>
                    <Typography className={classes.cardTitle}>
                      Creator Retention (30/60/90 days)
                    </Typography>
                    <Typography>Base cohort: {base} creators</Typography>
                    {base === 0 ? (
                      <Box mt={2} style={{ opacity: 0.7 }}>
                        Insufficient data for the last 90 days.
                      </Box>
                    ) : (
                      <div style={{ width: "100%", height: 170 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={retentionData}
                            margin={{ top: 8, right: 12, left: 4, bottom: 20 }}
                          >
                            <CartesianGrid
                              stroke="#27272a"
                              strokeDasharray="3 3"
                            />
                            <XAxis
                              dataKey="label"
                              tick={{ fontSize: 12, fill: "#a1a1aa" }}
                            />
                            <YAxis
                              domain={[0, 100]}
                              ticks={[0, 25, 50, 75, 100]}
                              tick={{ fontSize: 12, fill: "#a1a1aa" }}
                              tickFormatter={v => `${v}%`}
                            />
                            <RechartsTooltip
                              content={<RetentionTooltip />}
                              cursor={false} // або: cursor={{ fill: 'transparent' }}
                            />
                            <Bar
                              dataKey="pct"
                              fill="#FE5A97"
                              radius={[10, 10, 10, 10]}
                              background={{ fill: "#1f1f23", radius: 10 }}
                              maxBarSize={64}
                            >
                              <LabelList
                                dataKey="pct"
                                position="insideTop"
                                formatter={(v: number) => `${v}%`}
                                style={{ fill: "#fff", fontSize: 12 }}
                              />
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </div>
          </Grid>
          <Box className={classes.filterAdminHelp}>
            <TextField
              select
              variant="outlined"
              size="small"
              label="Context"
              className={classes.filterField}
              value={by}
              onChange={e => setBy(e.target.value)}
              InputLabelProps={{ shrink: true }}
            >
              {contexts.map(c => (
                <MenuItem
                  key={c.value}
                  value={c.value}
                  className={classes.menuItem}
                >
                  {c.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              variant="outlined"
              size="small"
              type="date"
              className={classes.filterField}
              label="From"
              value={from}
              onChange={e => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              variant="outlined"
              size="small"
              type="date"
              label="To"
              className={classes.filterField}
              value={to}
              onChange={e => setTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              variant="outlined"
              size="small"
              label="CreatorId"
              className={classes.filterField}
              value={creatorId}
              onChange={e => setCreatorId(e.target.value)}
              placeholder="5f... ObjectId"
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="contained"
              className={classes.applyFilterButton}
              onClick={applyFilters}
            >
              Apply
            </Button>
          </Box>
          {/* Centre Row: Donut + Revenue */}
          <Grid style={{ marginTop: "10px" }} className={classes.centre}>
            <Grid className={classes.centreLeft}>
              <Box className={classes.centreCard}>
                <div className={classes.cardHelpTop}>
                  <Typography className={classes.cardTitle}>
                    Earnings Overview
                  </Typography>
                </div>
                <div className={classes.centreCardHelp}>
                  <div className={classes.doughnutContainer}>
                    <MultiRingChart data={donutData} />
                  </div>
                  <Box className={classes.legend}>
                    <Box className={classes.legendItem}>
                      <span
                        className={classes.legendColor}
                        style={{ backgroundColor: "#FE5A97" }}
                      />
                      <Typography>Tips</Typography>
                      <Typography className={classes.legendValue}>
                        {donutData.total
                          ? `${Math.round(
                              (donutData.tips / donutData.total) * 100
                            )}%`
                          : "0%"}
                      </Typography>
                    </Box>
                    <Box className={classes.legendItem}>
                      <span
                        className={classes.legendColor}
                        style={{ backgroundColor: "#FFD700" }}
                      />
                      <Typography>Subscriptions</Typography>
                      <Typography className={classes.legendValue}>
                        {donutData.total
                          ? `${Math.round(
                              (donutData.subscriptions / donutData.total) * 100
                            )}%`
                          : "0%"}
                      </Typography>
                    </Box>
                    <Box className={classes.legendItem}>
                      <span
                        className={classes.legendColor}
                        style={{ backgroundColor: "#808080" }}
                      />
                      <Typography>Custom Video</Typography>
                      <Typography className={classes.legendValue}>
                        {donutData.total
                          ? `${Math.round(
                              (donutData.customVideoRequests /
                                donutData.total) *
                                100
                            )}%`
                          : "0%"}
                      </Typography>
                    </Box>
                    <Box className={classes.legendItem}>
                      <span
                        className={classes.legendColor}
                        style={{ backgroundColor: "#4682B4" }}
                      />
                      <Typography>Posts</Typography>
                      <Typography className={classes.legendValue}>
                        {donutData.total
                          ? `${Math.round(
                              (donutData.sales / donutData.total) * 100
                            )}%`
                          : "0%"}
                      </Typography>
                    </Box>
                  </Box>
                </div>
              </Box>
            </Grid>
            <Grid className={classes.centreRight}>
              <Box className={classes.centreCardBot}>
                <div className={classes.cardHelpTop}>
                  <Typography className={classes.cardTitle}>Revenue</Typography>
                  <Typography className={classes.dateRange}>
                    {periodLabel}
                  </Typography>
                </div>
                <div className={classes.cardHelp}>
                  <Typography className={classes.cardValue}>
                    {revenueLoading ? "..." : formatCurrency(totalRevenue)}
                  </Typography>
                  <Typography className={classes.cardChange}>
                    <TrendingUpIcon fontSize="small" /> platform-wide
                  </Typography>
                </div>
                <div>
                  <StackedRevenueChart
                    data={stackedRevenue}
                    loading={revenueLoading}
                  />
                </div>
              </Box>
            </Grid>
          </Grid>
          {/* Payout Status Overview */}
          <Grid container spacing={3} className={classes.centre}>
            <Grid item xs={12} className={classes.monthlyEarningsGridCard}>
              <Box
                className={`${classes.statCard} ${classes.statCardCompact}`}
              >
                <Typography className={classes.cardTitle}>
                  Payout Status Overview
                </Typography>
                {payoutsLoading ? (
                  <Typography>Loading…</Typography>
                ) : (
                  <>
                    <Grid container spacing={1}>
                      {[
                        {
                          key: "processed",
                          label: "Processed",
                          color: "#22c55e",
                        },
                        { key: "pending", label: "Pending", color: "#f59e0b" },
                        { key: "failed", label: "Failed", color: "#ef4444" },
                      ].map(s => {
                        const v = (payouts?.totals as any)?.[s.key] || {
                          amount: 0,
                          tx: 0,
                        };
                        return (
                          <Grid item xs={4} key={s.key}>
                            <Box style={{ borderColor: s.color }}>
                              <div>{s.label}</div>
                              <div>{formatCurrency(v.amount || 0)}</div>
                              <div>{(v.tx || 0).toLocaleString()} tx</div>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                    <Box mt={1}>
                      Total: {formatCurrency(payouts?.totals?.all?.amount || 0)}{" "}
                      • {(payouts?.totals?.all?.tx || 0).toLocaleString()} tx
                    </Box>
                  </>
                )}
              </Box>
            </Grid>
            {/* Average Earnings per Creator */}
            <Grid item xs={12} className={classes.monthlyEarningsGridCard}>
              <Box
                className={`${classes.statCard} ${classes.statCardCompact}`}
              >
                <Typography className={classes.cardTitle}>
                  Average Earnings per Creator
                </Typography>
                {avgEarningsLoading ? (
                  <Typography>Loading…</Typography>
                ) : (
                  <>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Box>
                          <div>Per active creator</div>
                          <div>
                            {formatCurrency(
                              avgEarnings?.averages?.perActiveCreator || 0
                            )}
                          </div>
                        </Box>
                      </Grid>
                      {avgEarnings?.averages?.perCreatorOverall != null && (
                        <Grid item xs={6}>
                          <Box>
                            <div>Per creator (overall)</div>
                            <div>
                              {formatCurrency(
                                avgEarnings?.averages?.perCreatorOverall || 0
                              )}
                            </div>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                    <Box mt={1}>
                      Gross: {formatCurrency(avgEarnings?.gross || 0)} • Active
                      creators:{" "}
                      {(avgEarnings?.activeCreators || 0).toLocaleString()}
                    </Box>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
          {/* Footer: Top lists */}
          <Grid className={classes.fotter}>
            <Grid className={classes.fotterLeft}>
              <Box className={classes.subscribersBox}>
                <Typography className={classes.sectionTitle}>
                  Top Creators
                </Typography>
                <Box className={classes.subscribersBoxx}>
                  {topLoading ? (
                    <CircularProgress />
                  ) : (
                    topCreators.map((c, i) => (
                      <Box key={i} className={classes.subscriberItem}>
                        <div className={classes.subscriberItemm}>
                          <Avatar
                            src={
                              mediaUrls[c.profileImg || ""] || defaultAvatars
                            }
                            className={classes.avatar}
                          >
                            {loadingMedia[c.profileImg || ""] && (
                              <CircularProgress size={24} />
                            )}
                          </Avatar>
                          <Box>
                            <Typography className={classes.name}>
                              {c.name}
                            </Typography>
                            <Typography className={classes.username}>
                              @{c.username}
                            </Typography>
                          </Box>
                        </div>
                        <Typography className={classes.likes}>
                          ${(c.total || 0).toLocaleString()}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
                <div className={classes.subscriberItemBot}>View All</div>
              </Box>
            </Grid>
            <Grid className={classes.fotterRight}>
              <Box className={classes.earningsBox}>
                <Typography className={classes.sectionTitle}>
                  Top Spenders
                </Typography>
                <Box className={classes.subscribersBoxx}>
                  {topLoading ? (
                    <CircularProgress />
                  ) : (
                    topSpenders.map((u, i) => (
                      <Box key={i} className={classes.earningItem}>
                        <div className={classes.subscriberItemm}>
                          <Avatar
                            src={
                              mediaUrls[u.profileImg || ""] || defaultAvatars
                            }
                            className={classes.avatar}
                          >
                            {loadingMedia[u.profileImg || ""] && (
                              <CircularProgress size={24} />
                            )}
                          </Avatar>
                          <Box>
                            <Typography className={classes.name}>
                              {u.name}
                            </Typography>
                            <Typography className={classes.username}>
                              @{u.username}
                            </Typography>
                          </Box>
                        </div>
                        <Typography className={classes.earnings}>
                          ${(u.total || 0).toLocaleString()}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
                <div className={classes.subscriberItemBot}>View All</div>
              </Box>
            </Grid>
          </Grid>
          <Grid
            style={{ columnGap: "125px", marginTop: "30px" }}
            className={classes.centre}
          >
            <Grid
              className={classes.monthlyEarningsGridCard}
              style={{ flex: "1 1 24%", minWidth: 260 }}
            >
              <Box className={classes.statCard}>
                <div
                  className={classes.cardHelpTop}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography className={classes.cardTitle}>
                    Active Creators Today
                  </Typography>
                </div>
                <div className={classes.cardBottom}>
                  <div className={classes.cardHelp}>
                    <Typography className={classes.cardValue}>
                      {activeTodayLoading
                        ? "..."
                        : activeCreatorsToday.toLocaleString()}
                    </Typography>
                    <Typography className={classes.cardChange}>
                      unique creators
                    </Typography>
                  </div>
                </div>
              </Box>
            </Grid>
            <Grid
              className={classes.monthlyEarningsGridCard}
              style={{ flex: "1 1 100%", maxWidth: "100%" }}
            >
              <Box style={{ height: 350 }} className={classes.statCard}>
                <div
                  className={classes.cardHelpTop}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography className={classes.cardTitle}>
                    Content Upload Volume
                  </Typography>

                  {/* кнопки з п.1 */}
                  <ButtonGroup
                    size="small"
                    variant="outlined"
                    className={classes.butTimeGroup}
                  >
                    <Button
                      onClick={() => setUploadsMode("daily")}
                      variant={
                        uploadsGranularity === "daily"
                          ? "contained"
                          : "outlined"
                      }
                      className={classes.butTime}
                    >
                      Daily
                    </Button>
                    <Button
                      onClick={() => setUploadsMode("weekly")}
                      variant={
                        uploadsGranularity === "weekly"
                          ? "contained"
                          : "outlined"
                      }
                      className={classes.butTime}
                    >
                      Weekly
                    </Button>
                    <Button
                      onClick={() => setUploadsMode("monthly")}
                      variant={
                        uploadsGranularity === "monthly"
                          ? "contained"
                          : "outlined"
                      }
                      className={classes.butTime}
                    >
                      Monthly
                    </Button>
                  </ButtonGroup>
                </div>

                {/*/!* Totals *!/*/}
                {/*<Box display="flex" mb={1} flexWrap="wrap" gap={8}>*/}
                {/*    <Chip label={`Photos: ${uploadsTotals.photos}`} />*/}
                {/*    <Chip label={`Videos: ${uploadsTotals.videos}`} />*/}
                {/*    <Chip label={`Total: ${uploadsTotals.total}`} />*/}
                {/*</Box>*/}

                {/* Chart */}
                <div style={{ width: "100%", height: 260 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={uploadsChartData}
                      margin={{ top: 8, right: 12, left: 8, bottom: 28 }}
                    >
                      <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                      <XAxis
                        dataKey="label"
                        interval={0}
                        angle={-45}
                        height={34}
                        textAnchor="end"
                        tick={{ fontSize: 12, fill: "#a1a1aa" }}
                      />
                      <YAxis tick={{ fontSize: 12, fill: "#a1a1aa" }} />
                      <RechartsTooltip content={<UploadsTooltip />} />
                      <Line
                        type="monotone"
                        dataKey="photos"
                        stroke="#60A5FA"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="videos"
                        stroke="#F472B6"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Box>
            </Grid>
          </Grid>
          {/* Creator Activity Monitoring */}
          <Grid style={{ display: "flex", flexDirection: "column" }}>
            {/* Volume 24h */}
            <Grid>
              <Box>
                <Typography className={classes.cardTitle}>
                  Transaction Volume (Last 24h)
                </Typography>

                <div className={classes.cardBottom}>
                  <div className={classes.cardHelp}>
                    <Typography className={classes.cardValue}>
                      {transactionsLoading
                        ? "..."
                        : txn24hTotals.total.toLocaleString()}
                    </Typography>
                    <Typography className={classes.cardChange}>
                      {transactionsLoading
                        ? ""
                        : `Success: ${txn24hTotals.success} • Failed: ${txn24hTotals.failed}`}
                    </Typography>
                  </div>

                  <div style={{ width: "100%", height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={txn24hChartData}
                        margin={{ top: 4, right: 12, left: 8, bottom: 28 }}
                      >
                        <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                        <XAxis
                          dataKey="label"
                          interval={0}
                          angle={-45}
                          height={34}
                          textAnchor="end"
                          tick={{ fontSize: 12, fill: "#a1a1aa" }}
                        />
                        <YAxis tick={{ fontSize: 12, fill: "#a1a1aa" }} />
                        <RechartsTooltip
                          contentStyle={{
                            background: "#09090b",
                            border: "1px solid #27272a",
                            borderRadius: 12,
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="success"
                          stroke="#22c55e"
                          strokeWidth={2}
                          dot={false}
                          name="Success"
                        />
                        <Line
                          type="monotone"
                          dataKey="failed"
                          stroke="#ef4444"
                          strokeWidth={2}
                          dot={false}
                          name="Failed"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </Box>
            </Grid>

            {/* Volume 7d */}
            <Grid>
              <Box>
                <Typography className={classes.cardTitle}>
                  Transaction Volume (Last 7d)
                </Typography>
                <div className={classes.cardBottom}>
                  <div className={classes.cardHelp}>
                    <Typography className={classes.cardValue}>
                      {transactionsLoading
                        ? "..."
                        : txn7dTotals.total.toLocaleString()}
                    </Typography>
                    <Typography className={classes.cardChange}>
                      {transactionsLoading
                        ? ""
                        : `Success: ${txn7dTotals.success} • Failed: ${txn7dTotals.failed}`}
                    </Typography>
                  </div>
                  <div style={{ width: "100%", height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={txn7dChartData}
                        margin={{ top: 4, right: 12, left: 8, bottom: 28 }}
                      >
                        <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                        <XAxis
                          dataKey="label"
                          interval={0}
                          angle={-45}
                          height={34}
                          textAnchor="end"
                          tick={{ fontSize: 12, fill: "#a1a1aa" }}
                        />
                        <YAxis tick={{ fontSize: 12, fill: "#a1a1aa" }} />
                        <RechartsTooltip
                          contentStyle={{
                            background: "#09090b",
                            border: "1px solid #27272a",
                            borderRadius: 12,
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="success"
                          stroke="#22c55e"
                          strokeWidth={2}
                          dot={false}
                          name="Success"
                        />
                        <Line
                          type="monotone"
                          dataKey="failed"
                          stroke="#ef4444"
                          strokeWidth={2}
                          dot={false}
                          name="Failed"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </Box>
            </Grid>
          </Grid>
          {/* Compliance Snapshot */}
          <Grid className={classes.monthlyEarningsGrid}>
            <Typography className={classes.monthlyEarningsGridTitle}>
              Compliance Snapshot
            </Typography>
            <div className={classes.monthlyEarningsGridd}>
              <Grid className={classes.monthlyEarningsGridCard}>
                <Box style={{ padding: "12px" }} className={classes.statCard}>
                  <Typography className={classes.cardTitle}>
                    Creators 18+ Verified
                  </Typography>
                  <div className={classes.cardBottom}>
                    <div className={classes.cardHelp}>
                      <Typography className={classes.cardValue}>
                        {complianceLoading
                          ? "..."
                          : `${verifiedPercent.toFixed(2)}%`}
                      </Typography>
                      <Typography className={classes.cardChange}>
                        {complianceLoading
                          ? ""
                          : `${verifiedCreators} / ${totalCreators} creators`}
                      </Typography>
                    </div>
                    <MiniLineChart
                      dataPoints={[100, 100, 100]}
                      loading={false}
                    />
                  </div>
                </Box>
              </Grid>

              <Grid className={classes.monthlyEarningsGridCard}>
                <Box style={{ padding: "12px" }} className={classes.statCard}>
                  <Typography className={classes.cardTitle}>
                    Flagged Pending Review
                  </Typography>
                  <div className={classes.cardBottom}>
                    <div className={classes.cardHelp}>
                      <Typography className={classes.cardValue}>
                        {complianceLoading
                          ? "..."
                          : (compliance?.kpis.flaggedPending.total ?? 0)}
                      </Typography>
                      <Typography className={classes.cardChange}>
                        {complianceLoading
                          ? ""
                          : `Photos: ${
                              compliance?.kpis.flaggedPending.photos ?? 0
                            } • Videos: ${
                              compliance?.kpis.flaggedPending.videos ?? 0
                            }`}
                      </Typography>
                    </div>
                    <MiniLineChart dataPoints={[1, 2, 3]} loading={false} />
                  </div>
                </Box>
              </Grid>
              <Grid className={classes.monthlyEarningsGridCard}>
                <Box style={{ padding: "12px" }} className={classes.statCard}>
                  <Typography className={classes.cardTitle}>
                    Recent DMCA Takedowns
                  </Typography>
                  <div className={classes.cardBottom}>
                    <div className={classes.cardHelp}>
                      <Typography className={classes.cardValue}>
                        {complianceLoading ? "..." : dmcaCount}
                      </Typography>
                      <Typography className={classes.cardChange}>
                        last 30 days
                      </Typography>
                    </div>
                    <MiniLineChart dataPoints={[0, 1, 0]} loading={false} />
                  </div>
                </Box>
              </Grid>
              <Grid className={classes.monthlyEarningsGridCard}>
                <Box style={{ padding: "12px" }} className={classes.statCard}>
                  <Typography className={classes.cardTitle}>
                    Platform Uptime (30d)
                  </Typography>
                  <div className={classes.cardBottom}>
                    <div className={classes.cardHelp}>
                      <Typography className={classes.cardValue}>
                        {complianceLoading ? "..." : `${uptimePct.toFixed(2)}%`}
                      </Typography>
                      <Typography className={classes.cardChange}>
                        Based on downtime logs
                      </Typography>
                    </div>
                    <MiniLineChart
                      dataPoints={[99.9, 99.95, 99.96]}
                      loading={false}
                    />
                  </div>
                </Box>
              </Grid>
            </div>
            {/* Details: 2257 log + Downtime table */}
            {/*<Grid container spacing={2} style={{ marginTop: 8 }}>*/}
            {/*    /!* 2257 Custodian Record Log *!/*/}
            {/*    <Grid item xs={12} md={6}>*/}
            {/*        <Box className={classes.subscribersBox}>*/}
            {/*            <Typography className={classes.sectionTitle}>2257 Custodian Record Log</Typography>*/}
            {/*            <Box className={classes.subscribersBoxx}>*/}
            {/*                /!* Зведення: останній аудит + скільки бракує документів *!/*/}
            {/*                <Box className={classes.subscriberItem}>*/}
            {/*                    <div className={classes.subscriberItemm}>*/}
            {/*                        <Avatar className={classes.avatar}>A</Avatar>*/}
            {/*                        <Box>*/}
            {/*                            <Typography className={classes.name}>Last audit</Typography>*/}
            {/*                            <Typography className={classes.username}>*/}
            {/*                                {complianceLoading*/}
            {/*                                    ? "..."*/}
            {/*                                    : (lastAuditAt ? new Date(lastAuditAt).toLocaleString() : "—")}*/}
            {/*                            </Typography>*/}
            {/*                        </Box>*/}
            {/*                    </div>*/}
            {/*                    <Typography className={classes.likes}>*/}
            {/*                        Missing docs: {complianceLoading ? "..." : missingDocs}*/}
            {/*                    </Typography>*/}
            {/*                </Box>*/}
            {/*                /!* Останні дії у 2257 журналі *!/*/}
            {/*                {complianceLoading ? (*/}
            {/*                    <CircularProgress size={24} style={{ margin: 8 }} />*/}
            {/*                ) : custodianRecent.length === 0 ? (*/}
            {/*                    <Typography className={classes.username} style={{ padding: 12, opacity: 0.8 }}>*/}
            {/*                        No recent custodian records*/}
            {/*                    </Typography>*/}
            {/*                ) : (*/}
            {/*                    custodianRecent.slice(0, 8).map((r, i) => (*/}
            {/*                        <Box key={i} className={classes.earningItem}>*/}
            {/*                            <div className={classes.subscriberItemm}>*/}
            {/*                                <Avatar className={classes.avatar}>*/}
            {/*                                    {(r.action || "L").slice(0, 1).toUpperCase()}*/}
            {/*                                </Avatar>*/}
            {/*                                <Box>*/}
            {/*                                    <Typography className={classes.name}>{r.action}</Typography>*/}
            {/*                                    <Typography className={classes.username}>*/}
            {/*                                        {new Date(r.at).toLocaleString()} {r.note ? `— ${r.note}` : ""}*/}
            {/*                                    </Typography>*/}
            {/*                                </Box>*/}
            {/*                            </div>*/}
            {/*                            /!* короткий ідентифікатор креатора *!/*/}
            {/*                            <Typography className={classes.earnings}>*/}
            {/*                                #{(r.creatorId || "").toString().slice(-6)}*/}
            {/*                            </Typography>*/}
            {/*                        </Box>*/}
            {/*                    ))*/}
            {/*                )}*/}
            {/*            </Box>*/}
            {/*        </Box>*/}
            {/*    </Grid>*/}
            {/*    /!* Downtime Logs *!/*/}
            {/*    <Grid item xs={12} md={6}>*/}
            {/*        <Box className={classes.subscribersBox}>*/}
            {/*            <Typography className={classes.sectionTitle}>*/}
            {/*                Downtime Logs ({downtimeDays}d)*/}
            {/*            </Typography>*/}

            {/*            <Box className={classes.subscribersBoxx}>*/}
            {/*                {complianceLoading ? (*/}
            {/*                    <CircularProgress size={24} style={{ margin: 8 }} />*/}
            {/*                ) : downtimeEvents.length === 0 ? (*/}
            {/*                    <Typography className={classes.username} style={{ padding: 12, opacity: 0.8 }}>*/}
            {/*                        No incidents logged*/}
            {/*                    </Typography>*/}
            {/*                ) : (*/}
            {/*                    downtimeEvents.slice(0, 10).map((e, i) => (*/}
            {/*                        <Box key={i} className={classes.subscriberItem}>*/}
            {/*                            <div className={classes.subscriberItemm}>*/}
            {/*                                <Avatar className={classes.avatar}>D</Avatar>*/}
            {/*                                <Box>*/}
            {/*                                    <Typography className={classes.name}>{e.reason || "Downtime"}</Typography>*/}
            {/*                                    <Typography className={classes.username}>*/}
            {/*                                        {new Date(e.start).toLocaleString()} —{" "}*/}
            {/*                                        {e.end ? new Date(e.end).toLocaleString() : "ongoing"}*/}
            {/*                                    </Typography>*/}
            {/*                                </Box>*/}
            {/*                            </div>*/}
            {/*                            <Typography className={classes.likes}></Typography>*/}
            {/*                        </Box>*/}
            {/*                    ))*/}
            {/*                )}*/}
            {/*            </Box>*/}
            {/*        </Box>*/}
            {/*    </Grid>*/}
            {/*</Grid>*/}
          </Grid>
          {/* USER IP HISTORY MODAL */}
          <Dialog
            open={userIpModal.open}
            onClose={closeUserIpHistory}
            fullWidth
            maxWidth="md"
            PaperProps={{ style: { background: "#0B0B0F", color: "#fff" } }}
          >
            <DialogTitle
              style={{
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>
                User IP history
                {userIpModal.username ? ` — ${userIpModal.username}` : ""}
              </span>
              <IconButton onClick={closeUserIpHistory} size="small">
                <CloseIcon style={{ color: "#fff" }} />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers style={{ color: "#fff" }}>
              {userIpLoading ? (
                <Box py={4} display="flex" justifyContent="center">
                  <CircularProgress />
                </Box>
              ) : userIpError ? (
                <Box py={2} style={{ color: "#f87171" }}>
                  {userIpError}
                </Box>
              ) : (userIpHistory?.length ?? 0) === 0 ? (
                <Box py={2} style={{ opacity: 0.7, color: "#fff" }}>
                  No IPs found for this user.
                </Box>
              ) : (
                <>
                  <Table size="small" style={{ color: "#fff" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ color: "#fff" }}>
                          Last Seen
                        </TableCell>
                        <TableCell style={{ color: "#fff" }}>IP</TableCell>
                        <TableCell style={{ color: "#fff" }}>
                          User-Agent
                        </TableCell>
                        <TableCell align="right" style={{ color: "#fff" }}>
                          Hits
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {userIpHistory.map((row, idx) => (
                        <TableRow key={`${row.IP}-${row.UA ?? ""}-${idx}`}>
                          <TableCell style={{ color: "#fff" }}>
                            {fmtDateTime(row.Time)}
                          </TableCell>
                          <TableCell style={{ color: "#fff" }}>
                            <Box
                              display="flex"
                              alignItems="center"
                              style={{
                                fontFamily:
                                  "ui-monospace, SFMono-Regular, Menlo, monospace",
                                color: "#fff",
                              }}
                            >
                              <span>{row.IP}</span>
                              <MuiTooltip title="q">
                                <IconButton
                                  size="small"
                                  onClick={() => copyToClipboard(row.IP)}
                                >
                                  <FileCopyIcon
                                    fontSize="small"
                                    style={{ color: "#fff" }}
                                  />
                                </IconButton>
                              </MuiTooltip>
                            </Box>
                          </TableCell>
                          <TableCell
                            title={row.UA || ""}
                            style={{
                              color: "#fff",
                              maxWidth: 420,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {row.UA || "—"}
                          </TableCell>
                          <TableCell align="right" style={{ color: "#fff" }}>
                            {row.Hits}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Divider
                    style={{
                      marginTop: 12,
                      background: "rgba(255,255,255,0.12)",
                    }}
                  />
                  <Box
                    pt={1}
                    style={{ opacity: 0.75, fontSize: 12, color: "#fff" }}
                  >
                    Showing last {userIpHistory.length} IP entries.
                  </Box>
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </Box>
    </>
  );
};

export default AdminAnalyticsPage;
